import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Search, Mail } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { queryClient, apiRequest, setCsrfToken } from "@/lib/queryClient";
import { useQuery } from "@tanstack/react-query";
import SignupWelcome from "@/pages/SignupWelcome";
import { getBillingPlanName } from "@shared/billingPermissions";

export default function SignupSuccess() {
  const [, setLocation] = useLocation();
  const { user, isLoading: authLoading } = useAuth();
  const [showConfetti, setShowConfetti] = useState(true);
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [paymentConfirmationFailed, setPaymentConfirmationFailed] = useState(false);

  const { isLoading: verificationSettingLoading } = useQuery<{ verificationRequired: boolean }>({
    queryKey: ['/api/site-settings/verification-required'],
  });

  useEffect(() => {
    const timer = setTimeout(() => setShowConfetti(false), 3000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("setup") === "complete") {
      const sessionId = params.get("session_id");
      const confirmSetup = async () => {
        setPaymentConfirmationFailed(false);
        try {
          // Pre-fetch a fresh CSRF token before calling confirm.
          // This avoids a first-call 403 that happens when the page loads
          // fresh after a Stripe redirect (no token available yet).
          const csrfRes = await fetch("/api/csrf-token", { credentials: "include" });
          if (csrfRes.ok) {
            const csrfData = await csrfRes.json();
            if (csrfData?.csrfToken) setCsrfToken(csrfData.csrfToken);
          }
          await apiRequest("POST", "/api/confirm-payment-setup", { sessionId });
          await queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
          setPaymentConfirmed(true);
        } catch (err) {
          console.error("Failed to confirm payment setup:", err);
          setPaymentConfirmed(false);
          setPaymentConfirmationFailed(true);
        }
      };
      confirmSetup();
    }
  }, []);

  if (authLoading) {
    return <div className="min-h-[100dvh] bg-background" aria-busy="true" />;
  }

  if (!user) {
    setLocation("/");
    return null;
  }

  const isShipperStripeReturn =
    user.userType === "Shipper" &&
    new URLSearchParams(window.location.search).get("setup") === "complete";
  if (isShipperStripeReturn) {
    if (!paymentConfirmed) {
      return (
        <div className="min-h-[100dvh] bg-background flex items-center justify-center p-6">
          <div className="text-center" role="status" aria-live="polite">
            {paymentConfirmationFailed ? (
              <>
                <p className="font-medium">We couldn't confirm your payment setup.</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Refresh this page to try again. Your partner results are saved for this signup session.
                </p>
              </>
            ) : (
              <>
                <div className="mx-auto mb-4 h-10 w-10 animate-pulse rounded-full bg-primary/20" />
                <p className="text-sm text-muted-foreground">Confirming your payment setup…</p>
              </>
            )}
          </div>
        </div>
      );
    }
    return <SignupWelcome />;
  }

  if (verificationSettingLoading) return null;

  const displayLabel = getBillingPlanName(user);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50 flex items-center justify-center">
          <CheckCircle2 className="w-32 h-32 text-primary animate-bounce" />
        </div>
      )}
      
      <Card className="max-w-2xl w-full">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <CheckCircle2 className="w-16 h-16 text-primary" data-testid="icon-success" />
          </div>
          <CardTitle className="text-3xl tracking-wide">
            Welcome to Fill The Trucks!
          </CardTitle>
          <CardDescription className="text-lg mt-2">
            Your profile is complete and you're ready to start
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          <div className="bg-muted/50 rounded-lg p-4">
            <p className="text-sm text-muted-foreground mb-2">Your membership:</p>
            <p className="text-xl font-semibold">
              {displayLabel}
            </p>
            {user.membershipTier === "Free" && (
              <p className="text-sm text-muted-foreground mt-1">
                You're on the Free plan. Upgrade anytime from Settings to unlock unlimited access.
              </p>
            )}
          </div>

          <div className="space-y-3">
            <h3 className="font-semibold text-lg">What would you like to do first?</h3>
            
            <Button 
              className="w-full justify-start" 
              size="lg"
              variant="outline"
              onClick={() => setLocation(user.userType === "Carrier" ? "/find-loads" : "/find-trucks")}
              data-testid="button-browse-marketplace"
            >
              <Search className="w-5 h-5 mr-2" />
              Browse the Marketplace
            </Button>

            <Button 
              className="w-full justify-start" 
              size="lg"
              onClick={() => setLocation("/dashboard")}
              data-testid="button-go-dashboard"
            >
              <CheckCircle2 className="w-5 h-5 mr-2" />
              Go to Dashboard
            </Button>
          </div>

          {!user.emailVerified && (
            <div className="pt-4 border-t">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Mail className="w-4 h-4" />
                <span>We sent a verification email to <strong>{user.email}</strong>. Please click the link to verify your email address.</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
